import { CheckCircle, Eye, EyeOff, Loader2, Lock, XCircle } from 'lucide-react'
import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SplitScreenAuthLayout } from '@/components/layout/SplitScreenAuthLayout'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { routePaths } from '@/lib/router'
import { authService } from '@/services/auth'

interface FormData {
  password: string
  confirmPassword: string
}

interface FormErrors {
  password?: string
  confirmPassword?: string
  submit?: string
}

const initialFormData: FormData = {
  password: '',
  confirmPassword: '',
}

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [errors, setErrors] = useState<FormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)
  const [tokenExpired, setTokenExpired] = useState(false)
  const [isValidating, setIsValidating] = useState(true)

  const token = searchParams.get('token')

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false)
        setIsValidating(false)

        return
      }

      try {
        const result = await authService.validateResetToken({ token })

        setTokenValid(result.valid)
        setTokenExpired(result.expired)
      } catch {
        setTokenValid(false)
      } finally {
        setIsValidating(false)
      }
    }

    validateToken()
  }, [token])

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!token) {
      setErrors({
        submit: 'Invalid reset link. Please request a new password reset.',
      })
      return
    }

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      await authService.resetPassword({
        token,
        password: formData.password,
        passwordConfirmation: formData.password,
      })

      setIsSuccess(true)
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Password reset failed'

      if (
        errorMsg.toLowerCase().includes('invalid') ||
        errorMsg.toLowerCase().includes('expired')
      ) {
        setTokenValid(false)
      } else {
        setErrors({ submit: errorMsg })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange =
    (field: keyof FormData) => (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    }

  const handleBackToLogin = () => {
    navigate(routePaths.login)
  }

  // Validating token state
  if (isValidating) {
    return (
      <SplitScreenAuthLayout>
        <div className="space-y-12">
          <div className="space-y-3 text-left">
            <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
              Validating Reset Link
            </h1>
            <p className="text-[14px] leading-[20px] text-muted-foreground">
              Please wait while we verify your reset link
            </p>
          </div>

          <Card className="w-full">
            <CardContent className="pt-6 text-center space-y-6">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold">Validating...</h3>
                <p className="text-muted-foreground">
                  Checking your password reset link.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </SplitScreenAuthLayout>
    )
  }

  // Invalid or expired token state
  if (tokenValid === false) {
    const title = tokenExpired ? 'Reset Link Expired' : 'Invalid Reset Link'
    const description = tokenExpired
      ? 'For security reasons, password reset links expire after a short time. Please request a new password reset link to continue.'
      : 'The password reset link you clicked is invalid or has already been used. Please request a new password reset link.'

    return (
      <SplitScreenAuthLayout>
        <div className="space-y-12">
          <div className="space-y-3 text-left">
            <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
              {title}
            </h1>
            <p className="text-[14px] leading-[20px] text-muted-foreground">
              {description}
            </p>
          </div>

          <Card className="w-full">
            <CardContent className="pt-6 text-center space-y-6">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate(routePaths.forgotPassword)}
                  className="flex-1"
                >
                  Request New Link
                </Button>
                <Button
                  variant="outline"
                  onClick={handleBackToLogin}
                  className="flex-1"
                >
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </SplitScreenAuthLayout>
    )
  }

  // Success state
  if (isSuccess) {
    return (
      <SplitScreenAuthLayout>
        <div className="space-y-12">
          <div className="space-y-3 text-left">
            <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
              Password Reset Complete
            </h1>
            <p className="text-[14px] leading-[20px] text-muted-foreground">
              Your password has been successfully updated. You can now log in
              with your new password.
            </p>
          </div>

          <Card className="w-full">
            <CardContent className="pt-6 text-center space-y-6">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>

              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800">
                <Lock className="h-4 w-4 text-green-600" />
                <div className="text-green-800 dark:text-green-200">
                  <p className="font-medium">Security Tip</p>
                  <p className="text-sm mt-1">
                    Make sure to keep your new password secure and don't share
                    it with anyone.
                  </p>
                </div>
              </Alert>

              <Button onClick={handleBackToLogin} className="w-full" size="lg">
                Continue to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </SplitScreenAuthLayout>
    )
  }

  // Reset form
  return (
    <SplitScreenAuthLayout>
      <div className="space-y-12">
        <div className="space-y-3 text-left">
          <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
            Set New Password
          </h1>
          <p className="text-[14px] leading-[20px] text-muted-foreground">
            Enter your new password below
          </p>
        </div>

        <Card className="w-full">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground text-center">
                  Please choose a strong password for your account.
                </p>
              </div>

              {/* Password Field */}
              <div className="flex flex-col gap-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    placeholder="Enter your new password"
                    disabled={isLoading}
                    className={
                      errors.password ? 'border-red-500 pr-10' : 'pr-10'
                    }
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="flex flex-col gap-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    placeholder="Confirm your new password"
                    disabled={isLoading}
                    className={
                      errors.confirmPassword ? 'border-red-500 pr-10' : 'pr-10'
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-500">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-secondary rounded-lg p-4 text-sm">
                <p className="font-medium mb-2">Password Requirements:</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li
                    className={
                      formData.password.length >= 8 ? 'text-green-600' : ''
                    }
                  >
                    • At least 8 characters long
                  </li>
                  <li
                    className={
                      /(?=.*[a-z])/.test(formData.password)
                        ? 'text-green-600'
                        : ''
                    }
                  >
                    • Contains lowercase letter (a-z)
                  </li>
                  <li
                    className={
                      /(?=.*[A-Z])/.test(formData.password)
                        ? 'text-green-600'
                        : ''
                    }
                  >
                    • Contains uppercase letter (A-Z)
                  </li>
                  <li
                    className={
                      /(?=.*\d)/.test(formData.password) ? 'text-green-600' : ''
                    }
                  >
                    • Contains number (0-9)
                  </li>
                </ul>
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <Alert variant="destructive">{errors.submit}</Alert>
              )}

              {/* Submit Button */}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating Password...
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Update Password
                  </>
                )}
              </Button>

              {/* Back to Login */}
              <div className="text-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackToLogin}
                  className="w-full"
                  disabled={isLoading}
                >
                  Cancel and Return to Login
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </SplitScreenAuthLayout>
  )
}
